// RecordEditAttachmentList - MODULE
// ***********************************************************************************************************************
import downloadjs from "downloadjs";
import * as React from "react";
import * as attachmentService from "../../models/services/attachmentService";
import * as recordService from "../../models/services/recordService";
import { Translations } from "ui/Scripts/source/models/translations";
import { SaveDataObj } from "../../framework/saveData";
import { AppUtils } from "../../framework/appUtils";
import { AttachmentList } from "../attachment/attachmentList";
import { FileAttachment, IFileAttachment } from "ui/Scripts/source/models/common/fileAttachment";
import { EditItem } from "ui/Scripts/source/models/common/editItem";
import { Base } from "ui/Scripts/source/framework/base";
import { CameraPhoto } from "../framework/cameraPhoto";
import { SaveAttachmentBase64 } from "ui/Scripts/source/models/common/saveAttachmentBase64";

// RecordEditAttachmentList
export interface IRecordEditAttachmentListProp {
    isReadOnly?: boolean;
    companyId: string;
    recordId: string;
    subOwnerId?: string;
    userCode: string;
    category: number;
    attachments: IFileAttachment[];
    onShowCamera: (value: boolean) => void;
    onRemoved: (ids: string[]) => void;
    onAdded: (attachments: IFileAttachment[]) => void;
}

export interface IRecordEditAttachmentListState {
    cameraIds: string[];
    showCamera: boolean;
    showingCamera: boolean;
}

export class RecordEditAttachmentList extends React.Component<IRecordEditAttachmentListProp, IRecordEditAttachmentListState> {
    constructor(props: IRecordEditAttachmentListProp) {
        super(props);
        this.state = {
            cameraIds: [],
            showCamera: false,
            showingCamera: false
        };
    }

    componentDidMount(): void {
        const obj = this;
        Base.getCameraIds()
            .then(ids => {
                obj.setState({ cameraIds: ids });
            })
            .catch(() => {
                obj.setState({ cameraIds: [] });
            });
    }

    handleDownload = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const attachment = props.attachments.find(i => i.id === id);
        if (!attachment) return;
        if (attachment.file) {
            downloadjs(attachment.file, attachment.name);
            return;
        }
        if (attachment.isNew()) return;
        const fileName = await AppUtils.callService(() => attachmentService.getAttachment(id));
        if (!fileName) return;
        AppUtils.showSuccessMessage(String.format(Translations.AttachmentParameterDownloaded, fileName));
    };

    getRemoveSaveData = (props: IRecordEditAttachmentListProp, attachmentIds: string[]): SaveDataObj => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("ids", attachmentIds);
        return result;
    };

    handleRemove = async (id: string) => {
        if (!id) return;
        const props = this.props;
        const saveData = this.getRemoveSaveData(this.props, [id]);
        const result = AppUtils.callService(() => recordService.recordEditRemoveAttachments(saveData.obj));
        if (!result) return;
        props.onRemoved([id]);
    };

    getAddSaveData = async (props: IRecordEditAttachmentListProp, attachments: IFileAttachment[], isPhoto: boolean): Promise<SaveDataObj> => {
        const result = new SaveDataObj();
        result.append("id", props.recordId);
        result.append("subOwnerId", !props.subOwnerId ? null : props.subOwnerId);
        result.append("companyId", props.companyId);
        result.append("userCode", props.userCode);
        result.append("category", props.category);
        result.append("attachments", await SaveAttachmentBase64.fromFileAttachments(attachments.filter(i => !!i.file), isPhoto ? "photo" : ""));
        return result;
    };
    
    save = async (attachments: FileAttachment[], isPhoto: boolean): Promise<boolean> => {
        const props = this.props;
        if (attachments.length < 1) return false;
        const saveData = await this.getAddSaveData(this.props, attachments, isPhoto);
        const result = await AppUtils.callService(() => recordService.recordEditAddAttachments(saveData.obj));
        if (!result) return false;
        EditItem.setItemIdsFromIds(attachments, result.ids);
        props.onAdded(attachments);
        return true;
    };
    
    handleAdd = async (fileList: FileList) => {
        const props = this.props;
        this.save(await FileAttachment.addFileListToFileAttachments([], fileList, props.category, props.subOwnerId), false);
    };

    //#region Camera
    hasCameras = (): boolean => {
        return !Base.isNullOrUndefined(this.state.cameraIds) && this.state.cameraIds.length > 0;
    };
    
    handleCameraStreamAvailable = () => {
        this.setState({ showingCamera: true });
    };

    handleAddPhoto = () => {
        if (!this.hasCameras()) return;
        this.setState({ showCamera: true });
        this.props.onShowCamera(true);
    };

    handleCameraPhotoOk = async (dataUrl: string) => {
        if (!dataUrl) {
            this.setState({ showCamera: false, showingCamera: false });
            this.props.onShowCamera(false);
            return;
        }
        const attachment = await FileAttachment.photoToFileAttachment(dataUrl, 0);
        if (!attachment) return;
        if (!await this.save([attachment], true)) return;
        this.setState({ showCamera: false, showingCamera: false });
        this.props.onShowCamera(false);
    };

    handleCameraPhotoCancel = (permissionDenied: boolean) => {
        if (!permissionDenied) {
            this.setState({ showCamera: false, showingCamera: false });
        } else {
            this.setState({ showCamera: false, showingCamera: false, cameraIds: [] });
        }
        this.props.onShowCamera(false);
    };
    //#endregion Camera

    render() {
        const props = this.props;
        const state = this.state;
        const showCamera = state.cameraIds.length > 0 && state.showCamera;
        const showingCamera = showCamera && state.showingCamera;
        return (
            <div>
                {!showingCamera &&
                    <AttachmentList
                        canShowCamera={this.hasCameras()}
                        isReadOnly={props.isReadOnly}
                        attachments={props.attachments}
                        onRemoveAttachment={this.handleRemove}
                        onDownloadAttachment={this.handleDownload}
                        onAddAttachments={this.handleAdd}
                        onAddPhoto={this.handleAddPhoto}
                    />
                }
                {showCamera &&
                    <CameraPhoto
                        cameraIds={state.cameraIds}
                        cameraStreamAvailable={state.showingCamera}
                        onCameraStreamAvailable={this.handleCameraStreamAvailable}
                        onOkClick={this.handleCameraPhotoOk}
                        onCancelClick={this.handleCameraPhotoCancel}
                    />
                }
            </div>
        );
    }
}